const API_BASE_URL = process.env.REACT_APP_API_URL;
if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL environment variable is required');
}

export interface Ticket {
  id: string;
  user_id: string;
  title: string;
  description: string;
  image_url?: string;
  issue_type?: 'credits' | 'subscription' | 'billing' | 'technical' | 'other';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  admin_response?: string;
  created_at: string;
  updated_at?: string;
  resolved_at?: string;
}

export interface TicketCreate {
  title: string;
  description: string;
  image_url?: string;
  issue_type?: 'credits' | 'subscription' | 'billing' | 'technical' | 'other';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
}

export interface TicketWithUser extends Ticket {
  user_email?: string;
  user_first_name?: string;
  user_last_name?: string;
}

class TicketService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  async createTicket(ticket: TicketCreate): Promise<Ticket> {
    const response = await fetch(`${API_BASE_URL}/api/tickets`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(ticket)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create ticket');
    }

    return response.json();
  }

  async getMyTickets(): Promise<Ticket[]> {
    const response = await fetch(`${API_BASE_URL}/api/tickets`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch tickets');
    }

    return response.json();
  }

  async getTicket(ticketId: string): Promise<Ticket> {
    const response = await fetch(`${API_BASE_URL}/api/tickets/${ticketId}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch ticket');
    }

    return response.json();
  }

  // Admin methods
  async getAllTickets(statusFilter?: string, priorityFilter?: string, issueTypeFilter?: string): Promise<TicketWithUser[]> {
    const params = new URLSearchParams();
    if (statusFilter && statusFilter !== 'all') {
      params.append('status_filter', statusFilter);
    }
    if (priorityFilter && priorityFilter !== 'all') {
      params.append('priority_filter', priorityFilter);
    }
    if (issueTypeFilter && issueTypeFilter !== 'all') {
      params.append('issue_type_filter', issueTypeFilter);
    }
    
    const response = await fetch(`${API_BASE_URL}/api/admin/tickets?${params}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch tickets');
    }

    return response.json();
  }

  async getTicketAdmin(ticketId: string): Promise<TicketWithUser> {
    const response = await fetch(`${API_BASE_URL}/api/admin/tickets/${ticketId}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch ticket');
    }

    return response.json();
  }

  async updateTicketAdmin(ticketId: string, update: { status?: string; priority?: string; admin_response?: string }): Promise<Ticket> {
    const response = await fetch(`${API_BASE_URL}/api/admin/tickets/${ticketId}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(update)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update ticket');
    }

    return response.json();
  }

  async deleteTicketAdmin(ticketId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/admin/tickets/${ticketId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete ticket');
    }
  }
}

export const ticketService = new TicketService();
